import React from 'react';
import PropTypes from 'prop-types';
import { alpha, Box, Button, Card, CardContent, Grid, Stack, Typography, useTheme } from '@mui/material';

const IndicatorSummaryCard = ({
  title,
  caption,
  actionLabel,
  onAction,
  icon,
  indicators,
  maxIndicators = 4,
  gridBreakpoints = { xs: 12, md: 6 },
}) => {
  const theme = useTheme();
  const displayedIndicators = indicators.slice(0, maxIndicators);
  const columns = displayedIndicators.length > 2 ? 6 : 12;

  return (
    <Grid item {...gridBreakpoints}>
      <Card
        sx={{
          height: '100%',
          borderRadius: 3,
          boxShadow: '0 12px 32px rgba(15, 30, 65, 0.08)',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
        }}
      >
        <CardContent sx={{ p: 3, height: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {title}
              </Typography>
              {caption && (
                <Typography variant="h5" sx={{ mt: 0.5 }}>
                  {caption}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {actionLabel && onAction && (
                <Button size="small" color="primary" onClick={onAction} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {actionLabel}
                </Button>
              )}
              {icon && (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                  }}
                >
                  {icon}
                </Box>
              )}
            </Stack>
          </Stack>

          <Grid container spacing={1.5}>
            {displayedIndicators.map((indicator) => {
              const color = indicator.color || 'primary';
              const paletteColor = theme.palette[color] || theme.palette.primary;
              const valueToFormat =
                indicator.value === 0 || Boolean(indicator.value) ? indicator.value : null;
              const value =
                typeof valueToFormat === 'number'
                  ? valueToFormat.toLocaleString()
                  : valueToFormat || '—';

              return (
                <Grid item xs={12} sm={columns} key={indicator.label}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(paletteColor.main, 0.25)}`,
                      bgcolor: alpha(paletteColor.main, 0.06),
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {indicator.icon && (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(paletteColor.main, 0.18),
                            color: paletteColor.main,
                          }}
                        >
                          {React.cloneElement(indicator.icon, { fontSize: 'small' })}
                        </Box>
                      )}
                      <Box flexGrow={1} minWidth={0}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {indicator.label}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                          {value}
                        </Typography>
                      </Box>
                      {indicator.delta && (
                        <Typography
                          variant="caption"
                          color={indicator.deltaColor || (indicator.delta.startsWith('-') ? 'error.main' : 'success.main')}
                          sx={{ fontWeight: 600 }}
                        >
                          {indicator.delta}
                        </Typography>
                      )}
                    </Stack>
                    {indicator.helperText && (
                      <Typography variant="caption" color="text.secondary">
                        {indicator.helperText}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

IndicatorSummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  caption: PropTypes.string,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  icon: PropTypes.node,
  indicators: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      icon: PropTypes.node,
      color: PropTypes.string,
      helperText: PropTypes.string,
      delta: PropTypes.string,
      deltaColor: PropTypes.string,
    })
  ),
  maxIndicators: PropTypes.number,
  gridBreakpoints: PropTypes.object,
};

IndicatorSummaryCard.defaultProps = {
  caption: '',
  actionLabel: '',
  onAction: null,
  icon: null,
  indicators: [],
  maxIndicators: 4,
  gridBreakpoints: { xs: 12, md: 6 },
};

export default IndicatorSummaryCard;
